import { Link } from '@inertiajs/react';
import { ChevronRight } from 'lucide-react';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { truncateWords } from '../../lib/truncateWords';
import { cn } from '../../lib/cn';

/**
 * Renders the current breadcrumb trail as a row of linked labels separated by
 * chevrons. Nothing renders when the trail is empty so pages without context
 * get their usual scroll title treatment.
 *
 * Intended to be placed in a shell's top bar next to the scroll title.
 */
export default function BreadcrumbBar({ className, maxCharsPerLabel = 32 }) {
  const { crumbs } = useBreadcrumbs();
  if (!crumbs || crumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        'flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 min-w-0',
        className
      )}
    >
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        const label = typeof crumb.label === 'string'
          ? truncateWords(crumb.label, maxCharsPerLabel)
          : crumb.label;

        return (
          <span key={`${crumb.level}-${i}`} className="flex items-center gap-1 min-w-0">
            {crumb.href && !isLast ? (
              <Link
                href={crumb.href}
                className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors truncate"
              >
                {label}
              </Link>
            ) : (
              <span
                className={cn(
                  'truncate',
                  isLast
                    ? 'text-gray-900 dark:text-white font-medium'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                {label}
              </span>
            )}
            {!isLast && (
              <ChevronRight
                className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0"
                strokeWidth={1.5}
              />
            )}
          </span>
        );
      })}
    </nav>
  );
}
